import { Eye, EyeOff } from "lucide-react";

import { getContrastingClassTextColor, type AnnotationClassRegistry } from "@shared/annotationClassColors";

interface AnnotationClassControlsProps {
  registry: AnnotationClassRegistry;
  counts: Record<string, number>;
  hiddenClassNames: ReadonlySet<string>;
  selectedClassName?: string;
  onToggleVisibility: (className: string) => void;
  onSelectedClassChange?: (className: string) => void;
}

interface NewBoxClassControlProps {
  registry: AnnotationClassRegistry;
  activeClassName: string;
  drawingDisabled?: boolean;
  onActiveClassChange: (className: string) => void;
}

const ClassSelect = ({
  label,
  visibleLabel = label,
  value,
  registry,
  onChange,
}: {
  label: string;
  visibleLabel?: string;
  value: string;
  registry: AnnotationClassRegistry;
  onChange: (value: string) => void;
}) => (
  <label className="block space-y-1.5">
    <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">{visibleLabel}</span>
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-xs font-medium text-slate-800 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200">
      {registry.classes.map((classDefinition) => (
        <option
          key={classDefinition.name}
          value={classDefinition.name}>
          {classDefinition.shortcut ? `${classDefinition.shortcut} · ` : ""}
          {classDefinition.name}
        </option>
      ))}
    </select>
  </label>
);

export const AnnotationClassControls = ({
  registry,
  counts,
  hiddenClassNames,
  selectedClassName,
  onToggleVisibility,
  onSelectedClassChange,
}: AnnotationClassControlsProps) => (
  <section
    data-testid="annotation-class-controls"
    className="space-y-3 text-slate-700">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Classes</h3>
        <p className="text-[11px] text-slate-400">Color and visibility</p>
      </div>
      <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
        {registry.classes.length} labels
      </span>
    </div>

    <div className="space-y-1">
      {registry.classes.map((classDefinition) => {
        const hidden = hiddenClassNames.has(classDefinition.name);
        return (
          <div
            key={classDefinition.name}
            className={`flex min-h-9 items-center gap-2 rounded-md border px-1.5 transition-colors ${
              hidden ? "border-transparent opacity-45" : "border-slate-100 bg-slate-50/70"
            }`}>
            <button
              type="button"
              aria-label={`${hidden ? "Show" : "Hide"} ${classDefinition.name}`}
              onClick={() => onToggleVisibility(classDefinition.name)}
              className="rounded p-0.5 text-slate-400 hover:bg-white hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-200">
              {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            <span
              aria-hidden
              className="flex h-5 min-w-5 items-center justify-center rounded-md border border-black/10 px-1 font-mono text-[10px] font-bold shadow-sm"
              style={{
                backgroundColor: classDefinition.color,
                color: getContrastingClassTextColor(classDefinition.color),
              }}>
              {classDefinition.shortcut ?? ""}
            </span>
            <span className="min-w-0 flex-1 truncate text-xs font-medium">{classDefinition.name}</span>
            <span className="min-w-6 rounded bg-white px-1 text-center font-mono text-[10px] tabular-nums text-slate-500">
              {counts[classDefinition.name] ?? 0}
            </span>
          </div>
        );
      })}
    </div>

    {selectedClassName && onSelectedClassChange && (
      <div className="border-t border-slate-200 pt-3">
        <ClassSelect
          label="Selected box class"
          value={selectedClassName}
          registry={registry}
          onChange={onSelectedClassChange}
        />
      </div>
    )}
  </section>
);

export const NewBoxClassControl = ({
  registry,
  activeClassName,
  drawingDisabled,
  onActiveClassChange,
}: NewBoxClassControlProps) => {
  const activeClass = registry.classes.find(({ name }) => name === activeClassName) ?? registry.classes[0];

  return (
    <section
      data-testid="new-box-class-control"
      className="space-y-3">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="h-3 w-3 shrink-0 rounded-sm border border-black/10 shadow-sm"
          style={{ backgroundColor: activeClass?.color }}
        />
        <div>
          <h3 className="text-sm font-semibold text-slate-900">New boxes</h3>
          <p className="text-[11px] leading-4 text-slate-400">Choose a class before drawing.</p>
        </div>
      </div>

      <ClassSelect
        label="Class for new boxes"
        visibleLabel="Class"
        value={activeClassName}
        registry={registry}
        onChange={onActiveClassChange}
      />

      {drawingDisabled && (
        <p className="text-[11px] font-medium leading-4 text-amber-700">
          Drawing paused. Choose a visible class to continue.
        </p>
      )}
    </section>
  );
};
